'use client';

// One /api/auth/me read per page load, shared by everything on a public page
// that needs to know who is looking (the marketing header's account links,
// analytics identity). Public pages are statically rendered, so the server
// cannot answer this without opting every page into per-request rendering —
// which is what LandingNav's old cookie read did to the entire marketing site.

import { useEffect, useState } from 'react';
import { ownsEverythingFromMe } from '@roomtreatment/core';

/** Raw /api/auth/me body, or null when signed out / unreachable. */
export interface MeResponse {
  id: number | string;
  email?: string;
  email_verified?: boolean;
  is_premium?: boolean;
  has_subscription?: boolean;
  credit_balance?: number;
  prefers_metric?: number;
  /** The entitlement vector (store offers); see core `ResolvedEntitlements`. */
  resolved_entitlements?: unknown;
}

let mePromise: Promise<MeResponse | null> | null = null;

export function fetchMe(): Promise<MeResponse | null> {
  if (!mePromise) {
    mePromise = fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && d.id != null ? (d as MeResponse) : null))
      .catch(() => null);
  }
  return mePromise;
}

/** Test seam: forget the memoised read. */
export function resetMeCache(): void {
  mePromise = null;
}

export interface SessionSummary {
  signedIn: boolean;
  /** Owns everything the store sells: the Unlimited offer, or a
   *  grandfathered subscription or lifetime purchase. */
  isPro: boolean;
  /** False until /api/auth/me has answered. */
  resolved: boolean;
}

const SIGNED_OUT: SessionSummary = { signedIn: false, isPro: false, resolved: false };

export function useSessionSummary(): SessionSummary {
  const [summary, setSummary] = useState<SessionSummary>(SIGNED_OUT);
  useEffect(() => {
    let cancelled = false;
    void fetchMe().then((me) => {
      if (cancelled) return;
      setSummary({
        signedIn: !!me,
        isPro: ownsEverythingFromMe(me),
        resolved: true,
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return summary;
}
